import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { payoutApi } from "services/api";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import { StripePaymentForm } from "../../components/StripePaymentForm";

const stripePublishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || "";
const stripePromise = stripePublishableKey ? loadStripe(stripePublishableKey) : null;

const PayoutDetail: React.FC = () => {
 const { payoutId } = useParams<{ payoutId: string }>();
 const navigate = useNavigate();
 const [payout, setPayout] = useState<any>(null);
 const [loading, setLoading] = useState(true);
 const [error, setError] = useState<string | null>(null);
 const [showPaymentForm, setShowPaymentForm] = useState(false);
 const [paymentClientSecret, setPaymentClientSecret] = useState<string | null>(null);
 const [processingPayment, setProcessingPayment] = useState(false);

 useEffect(() => {
  if (payoutId) {
   fetchPayout();
  }
 }, [payoutId]);

 const fetchPayout = async () => {
  try {
   const response = await payoutApi.getPayout(payoutId!);
   setPayout(response.data.payout);
  } catch (err: any) {
   setError(err.response?.data?.error || "Failed to load payout");
  } finally {
   setLoading(false);
  }
 };

 const handlePayNegativePayout = async () => {
  if (!payout || payout.amount >= 0) {
   return;
  }

  setProcessingPayment(true);
  setError(null);

  try {
   const response = await payoutApi.payNegativePayout(payoutId!);
   if (response.data.clientSecret) {
    setPaymentClientSecret(response.data.clientSecret);
    setShowPaymentForm(true);
   } else {
    setError("Failed to initialize payment");
   }
  } catch (err: any) {
   setError(err.response?.data?.error || "Failed to initialize payment");
  } finally {
   setProcessingPayment(false);
  }
 };

 const handlePaymentSuccess = async (_paymentIntentId: string) => {
  setShowPaymentForm(false);
  setPaymentClientSecret(null);
  await fetchPayout();
  alert("Payment successful! Your payout has been updated.");
 };

 const handlePaymentError = (error: string) => {
  setError(`Payment failed: ${error}`);
  setShowPaymentForm(false);
  setPaymentClientSecret(null);
 };

 if (loading) {
  return <div className="p-8">Loading payout details...</div>;
 }

 if (!payout) {
  return (
   <div className="p-8">
    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
     Payout not found
    </div>
   </div>
  );
 }

 const isNegative = payout.amount < 0;
 const breakdown = payout.breakdown || {
  revenue: payout.metadata?.revenue || 0,
  platformFees: payout.platformFeeTotal || 0,
  subscriptionFees: payout.subscriptionFeeTotal || 0,
  netAmount: payout.amount,
  isPositive: payout.amount >= 0,
 };

 return (
  <div className="p-8">
   <div className="mb-6">
    <button
     onClick={() => navigate("/admin/payouts")}
     className="text-indigo-600 hover:text-indigo-900 mb-4"
    >
     ← Back to Payouts
    </button>
    <h1 className="text-3xl font-bold">Payout Details</h1>
    <p className="text-gray-600 mt-2">
     Payout #{payout._id.toString().slice(-8).toUpperCase()}
    </p>
   </div>

   {error && (
    <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 ">
     {error}
    </div>
   )}

   {/* Negative Payout Alert */}
   {isNegative && payout.status === "pending" && (
    <div className="mb-6 p-6 bg-red-50 border border-red-200 ">
     <div className="flex items-start justify-between">
      <div className="flex-1">
       <h3 className="text-lg font-semibold text-red-900 mb-2">Payment Required</h3>
       <p className="text-sm text-red-700 mb-4">
        Your subscription fees exceed your revenue for this period. Please pay the outstanding amount to continue using the platform.
       </p>
       <div className="bg-white border border-red-300 p-4">
        <div className="text-2xl font-bold text-red-600">
         ${Math.abs(payout.amount).toFixed(2)} {payout.currency}
        </div>
        <div className="text-sm text-red-600 mt-1">Amount Due</div>
       </div>
      </div>
      <button
       onClick={handlePayNegativePayout}
       disabled={processingPayment}
       className="ml-4 px-6 py-3 bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 font-semibold"
      >
       {processingPayment ? "Processing..." : "Pay Now"}
      </button>
     </div>
    </div>
   )}

   {/* Payout Information */}
   <div className="bg-white shadow p-6 mb-6">
    <h2 className="text-xl font-semibold mb-4">Payout Information</h2>
    <dl className="grid grid-cols-2 gap-4">
     <div>
      <dt className="text-sm font-medium text-gray-500">Status</dt>
      <dd>
       <span
        className={`px-2 inline-flex text-xs leading-5 font-semibold ${
         payout.status === "completed"
          ? "bg-green-100 text-green-800"
          : payout.status === "processing"
          ? "bg-blue-100 text-blue-800"
          : payout.status === "failed"
          ? "bg-red-100 text-red-800"
          : "bg-yellow-100 text-yellow-800"
        }`}
       >
        {payout.status}
       </span>
      </dd>
     </div>
     <div>
      <dt className="text-sm font-medium text-gray-500">Amount</dt>
      <dd className={`text-lg font-semibold ${isNegative ? "text-red-600" : "text-green-600"}`}>
       {isNegative ? "-" : "+"}
       ${Math.abs(payout.amount).toFixed(2)} {payout.currency}
      </dd>
     </div>
     <div>
      <dt className="text-sm font-medium text-gray-500">Period Start</dt>
      <dd className="text-sm text-gray-900">
       {new Date(payout.periodStart).toLocaleDateString()}
      </dd>
     </div>
     <div>
      <dt className="text-sm font-medium text-gray-500">Period End</dt>
      <dd className="text-sm text-gray-900">
       {new Date(payout.periodEnd).toLocaleDateString()}
      </dd>
     </div>
     <div>
      <dt className="text-sm font-medium text-gray-500">Payout Method</dt>
      <dd className="text-sm text-gray-900 capitalize">
       {payout.payoutMethod?.replace("_", " ") || "N/A"}
      </dd>
     </div>
     {payout.processedAt && (
      <div>
       <dt className="text-sm font-medium text-gray-500">Processed At</dt>
       <dd className="text-sm text-gray-900">
        {new Date(payout.processedAt).toLocaleString()}
       </dd>
      </div>
     )}
     {payout.transactionId && (
      <div className="col-span-2">
       <dt className="text-sm font-medium text-gray-500">Transaction ID</dt>
       <dd className="text-sm text-gray-900 font-mono">{payout.transactionId}</dd>
      </div>
     )}
    </dl>
   </div>

   {/* Financial Breakdown */}
   <div className="bg-white shadow p-6 mb-6">
    <h2 className="text-xl font-semibold mb-4">Financial Breakdown</h2>
    <div className="space-y-3">
     <div className="flex justify-between items-center p-3 bg-green-50 ">
      <span className="font-medium text-gray-700">Revenue from Orders</span>
      <span className="text-lg font-semibold text-green-600">
       +${breakdown.revenue.toFixed(2)} {payout.currency}
      </span>
     </div>
     <div className="flex justify-between items-center p-3 bg-red-50 ">
      <span className="font-medium text-gray-700">Platform Fees</span>
      <span className="text-lg font-semibold text-red-600">
       -${breakdown.platformFees.toFixed(2)} {payout.currency}
      </span>
     </div>
     <div className="flex justify-between items-center p-3 bg-red-50 ">
      <span className="font-medium text-gray-700">Subscription Fees</span>
      <span className="text-lg font-semibold text-red-600">
       -${breakdown.subscriptionFees.toFixed(2)} {payout.currency}
      </span>
     </div>
     <div className="flex justify-between items-center p-4 bg-gray-50 border-2 border-gray-300">
      <span className="text-lg font-bold text-gray-900">Net Payout</span>
      <span className={`text-2xl font-bold ${isNegative ? "text-red-600" : "text-green-600"}`}>
       {isNegative ? "-" : "+"}
       ${Math.abs(breakdown.netAmount).toFixed(2)} {payout.currency}
      </span>
     </div>
    </div>
   </div>

   {/* Orders Included */}
   {payout.orders && payout.orders.length > 0 && (
    <div className="bg-white shadow p-6">
     <h2 className="text-xl font-semibold mb-4">
      Orders Included ({payout.orders.length})
     </h2>
     <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
       <thead className="bg-gray-50">
        <tr>
         <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
          Order Number
         </th>
         <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
          Total
         </th>
         <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
          Platform Fee
         </th>
         <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
          Store Amount
         </th>
        </tr>
       </thead>
       <tbody className="bg-white divide-y divide-gray-200">
        {payout.orders.map((order: any) => (
         <tr key={order._id}>
          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
           {order.orderNumber || order._id.toString().slice(-8)}
          </td>
          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
           ${(order.total || 0).toFixed(2)} {order.currency || payout.currency}
          </td>
          <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600">
           -${(order.platformFee || 0).toFixed(2)} {order.currency || payout.currency}
          </td>
          <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-green-600">
           ${((order.total || 0) - (order.platformFee || 0)).toFixed(2)} {order.currency || payout.currency}
          </td>
         </tr>
        ))}
       </tbody>
      </table>
     </div>
    </div>
   )}

   {/* Payment Form Modal */}
   {showPaymentForm && paymentClientSecret && stripePromise && (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
     <div className="bg-white  p-6 max-w-md w-full">
      <h3 className="text-xl font-bold mb-4">Pay Negative Payout</h3>
      <div className="mb-4 p-4 bg-red-50 border border-red-200 ">
       <div className="text-sm text-red-700 mb-2">
        <strong>Amount Due:</strong> ${Math.abs(payout.amount).toFixed(2)} {payout.currency}
       </div>
      </div>
      <Elements stripe={stripePromise} options={{ clientSecret: paymentClientSecret }}>
       <StripePaymentForm
        clientSecret={paymentClientSecret}
        amount={Math.abs(payout.amount)}
        currency={payout.currency || "USD"}
        onSuccess={handlePaymentSuccess}
        onError={handlePaymentError}
       />
      </Elements>
      <button
       onClick={() => {
        setShowPaymentForm(false);
        setPaymentClientSecret(null);
       }}
       className="mt-4 w-full px-4 py-2 bg-gray-200 text-gray-700 hover:bg-gray-300"
      >
       Cancel
      </button>
     </div>
    </div>
   )}
  </div>
 );
};

export default PayoutDetail;

